import { useTranslation } from 'react-i18next';
import { FieldLabel } from '@/components/editorial';
import { FilterChips } from '@/components/common/FilterChips';
import { labelFor } from '@/i18n/labels';
import {
  CATEGORY_ORDER,
  UI_STATUS_LABEL_KEY,
  UI_STATUS_ORDER,
  UI_URGENCY_LABEL_KEY,
  UI_URGENCY_ORDER,
  categoryLabel,
  type TaskFilterState,
  type UiStatus,
  type UiUrgency,
} from '@/lib/task-display';

/**
 * The four filter groups inside the Tasks toolbar popover (declutter
 * 2026-09-10, spec §4 C3/C4). Replaces the three chip rows `TaskFilters.tsx`
 * stacked above the list — the status pills carried counts, the shape
 * Health removed first. Chips here carry no numbers; the list beneath is
 * the count.
 */
interface TaskFilterPanelProps {
  filters: TaskFilterState;
  onChange: (f: TaskFilterState) => void;
}

const ANY = '';

function Group({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div role="group" aria-labelledby={id} className="space-y-2">
      <FieldLabel id={id} as="span">{label}</FieldLabel>
      {children}
    </div>
  );
}

export function TaskFilterPanel({ filters, onChange }: TaskFilterPanelProps) {
  const { t } = useTranslation('tasks');
  return (
    <>
      <Group id="task-filter-show" label={t('filter.show')}>
        <FilterChips
          value={filters.status}
          onChange={(v) => onChange({ ...filters, status: v as UiStatus })}
          options={(['all', ...UI_STATUS_ORDER] as UiStatus[]).map((s) => ({
            value: s,
            label: labelFor(UI_STATUS_LABEL_KEY, s, t),
          }))}
        />
      </Group>
      <Group id="task-filter-category" label={t('filter.category')}>
        <FilterChips
          value={filters.category ?? ANY}
          onChange={(v) => onChange({ ...filters, category: v === ANY ? undefined : v })}
          options={[
            { value: ANY, label: t('filter.any') },
            ...CATEGORY_ORDER.map((c) => ({ value: c, label: categoryLabel(c, t) })),
          ]}
        />
      </Group>
      <Group id="task-filter-urgency" label={t('filter.urgency')}>
        <FilterChips
          value={filters.urgency ?? ANY}
          onChange={(v) => onChange({ ...filters, urgency: v === ANY ? undefined : (v as UiUrgency) })}
          options={[
            { value: ANY, label: t('filter.any') },
            ...UI_URGENCY_ORDER.map((u) => ({ value: u, label: labelFor(UI_URGENCY_LABEL_KEY, u, t) })),
          ]}
        />
      </Group>
      <Group id="task-filter-recurring" label={t('filter.recurring')}>
        <FilterChips
          value={filters.showEveryOccurrence ? 'every' : 'collapsed'}
          onChange={(v) => onChange({ ...filters, showEveryOccurrence: v === 'every' })}
          options={[
            { value: 'collapsed', label: t('filter.oneCardPerSeries') },
            { value: 'every', label: t('filter.everyOccurrence') },
          ]}
        />
      </Group>
    </>
  );
}
